<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import { useChatStore } from '../../stores/chatStore';
import { useResizableColumn } from '../../composables/useResizableColumn';
import MatterContextLeftPane from './MatterContextLeftPane.vue';
import MatterContextSummaryTab from './MatterContextSummaryTab.vue';
import MatterContextEventsTab from './MatterContextEventsTab.vue';
import MatterContextEntitiesTab from './MatterContextEntitiesTab.vue';
import ContextMenu, { type ContextMenuItem } from '../common/ContextMenu.vue';
import DiscardChangesDialog from './DiscardChangesDialog.vue';

type TabKey = 'summary' | 'events' | 'entities';

const { t } = useI18n();
const store = useMatterContextModalStore();
const chatStore = useChatStore();
const activeTab = ref<TabKey>('summary');

const closeDiscardDialogVisible = ref(false);

// Defer the loading overlay so fast initial loads don't flash it — it only
// appears once loading has run longer than this. Form-disabling still keys off
// store.loadingExisting directly, so only the overlay is debounced.
const LOADING_OVERLAY_DELAY_MS = 150;
const showLoadingOverlay = ref(false);
let loadingOverlayTimer: ReturnType<typeof setTimeout> | null = null;

// Watch both the loading flag and the confirmed-published status: on an
// explicit open the status round-trip may resolve after loading has already
// begun, so re-evaluating when it lands lets the overlay appear correctly.
watch(
    [() => store.loadingExisting, () => chatStore.matterContextPublished],
    ([loading, published]) => {
        if (loadingOverlayTimer) {
            clearTimeout(loadingOverlayTimer);
            loadingOverlayTimer = null;
        }
        // The overlay only makes sense when there is saved context to load, so
        // gate it on a status we've actually confirmed as published. A
        // brand-new matter (status still null, or a real is_published:false
        // payload) has nothing to load, so it never flashes the
        // "Loading saved matter context…" overlay — this also closes the race
        // where the status round-trip hadn't resolved yet when loading began.
        if (loading && published) {
            loadingOverlayTimer = setTimeout(() => {
                showLoadingOverlay.value = true;
                loadingOverlayTimer = null;
            }, LOADING_OVERLAY_DELAY_MS);
        } else {
            showLoadingOverlay.value = false;
        }
    },
);

onUnmounted(() => {
    if (loadingOverlayTimer) clearTimeout(loadingOverlayTimer);
});

// Resizable left/right panes. The left pane gets an explicit width (clamped so
// neither pane gets too small); the right pane flexes to fill. fixedColumnsWidth
// accounts for the divider; on each open the width seeds to ~38% of the body.
const {
    tableEl: bodyEl,
    width: leftPaneWidth,
    startResize: startPaneResize,
} = useResizableColumn({
    initialWidth: 460,
    minWidth: 360,
    neighborMinWidth: 520,
    fixedColumnsWidth: 1,
});

watch(
    () => store.visible,
    async (visible) => {
        if (!visible) return;
        await nextTick();
        const w = bodyEl.value?.clientWidth ?? 0;
        if (w) leftPaneWidth.value = Math.max(360, Math.min(w - 521, Math.round(w * 0.38)));
    },
);

// Modal-wide drag-and-drop. Drag handlers fire on the outer modal so
// users can drop anywhere; the visual overlay sits over the right pane
// (Summary / tab body) per the design. Disabled only while a run is in
// flight — dropping new files onto loaded context stages them for a re-run.
const dragActive = ref(false);

const dropEnabled = computed(() => store.inputsEditable);

const isFileDrag = (event: DragEvent) => {
    // dragenter/over fire for any kind of payload; only treat OS-file
    // drags as drop candidates so text/HTML drags don't trigger the
    // overlay. Some browsers report this on dataTransfer.types.
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
};

const onModalDragEnter = (event: DragEvent) => {
    if (!dropEnabled.value || !isFileDrag(event)) return;
    event.preventDefault();
    dragActive.value = true;
};

const onModalDragOver = (event: DragEvent) => {
    if (!dropEnabled.value || !isFileDrag(event)) return;
    event.preventDefault();
    dragActive.value = true;
};

const onModalDragLeave = (event: DragEvent) => {
    // Only flip off when the cursor leaves the modal entirely, not when
    // it crosses internal nodes (relatedTarget = node entered next).
    const related = event.relatedTarget as Node | null;
    if (related && (event.currentTarget as Node).contains(related)) return;
    dragActive.value = false;
};

const onModalDrop = (event: DragEvent) => {
    if (!dropEnabled.value) {
        dragActive.value = false;
        return;
    }
    event.preventDefault();
    dragActive.value = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
        store.stageFiles(Array.from(files));
    }
};

const onClose = () => {
    if (store.hasUnsavedWork) {
        closeDiscardDialogVisible.value = true;
        return;
    }
    store.close();
};

const onCloseDiscardConfirm = () => {
    closeDiscardDialogVisible.value = false;
    store.close();
};

const onCloseDiscardCancel = () => {
    closeDiscardDialogVisible.value = false;
};

const tabsLocked = computed(() => store.extractionState !== 'complete');

// Switching to a review tab marks it visited, clearing its unvisited dot
// (purely visual — see the store). Summary needs no visit tracking.
const onSelectTab = (tab: TabKey) => {
    activeTab.value = tab;
    if (tab === 'events' || tab === 'entities') store.markTabVisited(tab);
};

// Explains why "Publish" is disabled, surfaced as a tooltip. Empty
// while publishing or once the button is enabled (no tooltip shown then).
const publishDisabledReason = computed(() => {
    if (store.publishing || store.canPublish) return '';
    if (store.extractionState !== 'complete') {
        return t('matterContext.modal.publishDisabledNoContext');
    }
    return t('matterContext.modal.publishDisabledNoChanges');
});

const showSkeleton = computed(() => store.extractionState === 'analyzing');

const headerContextMenu = ref<{ x: number; y: number } | null>(null);

const extractionHasRun = computed(() => {
    const status = chatStore.matterContextStatus as { has_run?: boolean; status?: string } | null;
    return !!status?.has_run && status.status === 'completed';
});

const onHeaderContextMenu = (event: MouseEvent) => {
    // Hold ctrl/cmd to bypass us and get the native browser menu.
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    headerContextMenu.value = { x: event.clientX, y: event.clientY };
};

const closeHeaderMenu = () => {
    headerContextMenu.value = null;
};

const headerMenuItems = computed<ContextMenuItem[]>(() => [
    {
        label: t('matterContext.modal.debugMenu'),
        icon: 'bug_report',
        disabled: !extractionHasRun.value,
        onSelect: () => {
            // Leave the matter-context modal open behind the debug overlay
            // so closing the debug viewer returns to where the user was.
            chatStore.fetchMatterContextDebug(store.targetMatterId ?? undefined);
        },
    },
    {
        label: t('matterContext.modal.deleteMenu'),
        icon: 'delete_forever',
        disabled: !extractionHasRun.value,
        onSelect: () => {
            deleteConfirmVisible.value = true;
        },
    },
]);

// Delete-all confirmation. The wipe is irreversible, so it always routes
// through this dialog before hitting the backend.
const deleteConfirmVisible = ref(false);
const deleting = ref(false);

const onDeleteConfirm = async () => {
    if (deleting.value) return;
    deleting.value = true;
    try {
        const ok = await chatStore.deleteMatterContext(store.targetMatterId ?? undefined);
        deleteConfirmVisible.value = false;
        if (ok) {
            // Reset the open modal to its blank idle state so it reflects the
            // now-empty matter and the user can immediately re-upload.
            store.resetInPlace();
            store.showToast(t('matterContext.modal.deletedToast'));
        } else {
            store.showToast(t('matterContext.modal.deleteFailedToast'));
        }
    } finally {
        deleting.value = false;
    }
};

const onDeleteCancel = () => {
    if (deleting.value) return;
    deleteConfirmVisible.value = false;
};

// Reset to Summary if the user is on a locked tab when extraction state
// changes (e.g. modal reopen after a previous run).
watch(tabsLocked, (locked) => {
    if (locked && activeTab.value !== 'summary') activeTab.value = 'summary';
});

// Refresh status when modal opens so the menu reflects current extraction
// state. Targets the modal's matter explicitly so this works even when
// the modal is opened from the matters landing page (where currentMatter is null).
onMounted(() => {
    if (store.visible) chatStore.fetchMatterContextStatus(store.targetMatterId ?? undefined);
});
watch(
    () => store.visible,
    (visible) => {
        if (visible) chatStore.fetchMatterContextStatus(store.targetMatterId ?? undefined);
    },
);
</script>

<template>
    <!-- The open animation only plays when the modal is opened automatically
         during onboarding (:css gates it); explicit user opens appear instantly. -->
    <Transition name="mc-modal-open" :css="store.openedAutomatically">
        <div
            v-if="store.visible"
            class="mc-modal-overlay"
            @dragenter="onModalDragEnter"
            @dragover="onModalDragOver"
            @dragleave="onModalDragLeave"
            @drop="onModalDrop"
        >
            <div class="mc-modal">
            <div v-if="dragActive" class="mc-modal-dropzone">
                <span class="material-icons mc-modal-dropzone-icon">file_upload</span>
                <span class="mc-modal-dropzone-label">{{ t('matterContext.modal.dropFiles') }}</span>
            </div>

            <div class="mc-modal-close-strip">
                <button class="mc-modal-close" type="button" @click="onClose">
                    <span class="material-icons" style="font-size: 18px">close</span>
                    <span>{{ t('common.close') }}</span>
                </button>
            </div>

            <div
                v-if="chatStore.matterContextRequired && !store.loadingExisting"
                class="mc-onboarding-banner"
            >
                <span class="material-icons-outlined mc-onboarding-icon" aria-hidden="true"
                    >info</span
                >
                <span class="mc-onboarding-text">
                    <span class="mc-onboarding-title">{{
                        t('matterContext.modal.onboardingBannerTitle')
                    }}</span>
                    {{ t('matterContext.modal.onboardingBannerBody') }}
                </span>
            </div>

            <div class="mc-modal-header" @contextmenu="onHeaderContextMenu">
                <div class="mc-modal-title">{{ t('matterContext.modal.title') }}</div>
                <span class="mc-publish-btn-wrap">
                    <button
                        class="mc-publish-btn"
                        :disabled="!store.canPublish || store.publishing"
                        type="button"
                        @click="store.publish()"
                    >
                        <span
                            v-if="store.publishing"
                            class="mc-publish-spinner"
                            aria-hidden="true"
                        ></span>
                        <span
                            v-else-if="publishDisabledReason"
                            class="material-icons mc-publish-hint-icon"
                            aria-hidden="true"
                        >help_outline</span>
                        {{ store.publishing ? t('matterContext.modal.publishing') : t('matterContext.modal.publish') }}
                    </button>
                    <span v-if="publishDisabledReason" class="mc-publish-tooltip" role="tooltip">
                        {{ publishDisabledReason }}
                    </span>
                </span>
            </div>

            <div ref="bodyEl" class="mc-modal-body">
                <div
                    v-if="showLoadingOverlay"
                    class="mc-loading-overlay"
                    role="status"
                    aria-live="polite"
                >
                    <div class="mc-loading-overlay-card">
                        <span class="mc-loading-overlay-spinner" aria-hidden="true"></span>
                        <div class="mc-loading-overlay-text">
                            <p class="mc-loading-overlay-title">{{ t('matterContext.modal.loadingTitle') }}</p>
                            <p class="mc-loading-overlay-subtext">
                                {{ t('matterContext.modal.loadingSubtext') }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="mc-modal-left" :style="{ width: leftPaneWidth + 'px' }">
                    <MatterContextLeftPane />
                </div>

                <div
                    class="mc-pane-resizer"
                    :style="{ left: leftPaneWidth + 'px' }"
                    :title="t('matterContext.common.dragToResize')"
                    @mousedown="startPaneResize"
                ></div>

                <div class="mc-modal-right">
                    <div class="mc-tab-strip">
                        <button
                            class="mc-tab"
                            :class="{ 'mc-tab-active': activeTab === 'summary' }"
                            type="button"
                            @click="activeTab = 'summary'"
                        >
                            {{ t('matterContext.modal.tabSummary') }}
                        </button>
                        <button
                            class="mc-tab"
                            :class="{
                                'mc-tab-active': activeTab === 'events',
                                'mc-tab-locked': tabsLocked,
                            }"
                            type="button"
                            :disabled="tabsLocked"
                            @click="onSelectTab('events')"
                        >
                            {{ t('matterContext.modal.tabEvents') }}
                            <span
                                v-if="store.events.length > 0 && !store.eventsVisited"
                                class="mc-tab-dot"
                            ></span>
                        </button>
                        <button
                            class="mc-tab"
                            :class="{
                                'mc-tab-active': activeTab === 'entities',
                                'mc-tab-locked': tabsLocked,
                            }"
                            type="button"
                            :disabled="tabsLocked"
                            @click="onSelectTab('entities')"
                        >
                            {{ t('matterContext.modal.tabEntities') }}
                            <span
                                v-if="store.entities.length > 0 && !store.entitiesVisited"
                                class="mc-tab-dot"
                            ></span>
                        </button>
                    </div>

                    <div
                        class="mc-tab-body"
                        :class="{ 'mc-tab-body-grid': activeTab !== 'summary' }"
                    >
                        <div v-if="showSkeleton" class="mc-tab-skeleton">
                            <div v-if="activeTab === 'summary'" class="mc-skeleton-label">
                                {{ t('matterContext.modal.generatingSummary') }}
                            </div>
                            <div class="mc-skeleton-row" style="width: 60%"></div>
                            <div class="mc-skeleton-row" style="width: 80%"></div>
                            <div class="mc-skeleton-row" style="width: 70%"></div>
                            <div class="mc-skeleton-row" style="width: 90%"></div>
                        </div>
                        <template v-else>
                            <MatterContextSummaryTab v-if="activeTab === 'summary'" />
                            <MatterContextEventsTab v-else-if="activeTab === 'events'" />
                            <MatterContextEntitiesTab v-else-if="activeTab === 'entities'" />
                        </template>
                    </div>

                    <div class="mc-modal-footer">
                        <p class="mc-disclaimer">
                            {{ t('matterContext.modal.disclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
    </Transition>

    <div v-if="store.toast" class="mc-toast" @click="store.dismissToast()">
        {{ store.toast }}
    </div>

    <ContextMenu :position="headerContextMenu" :items="headerMenuItems" @close="closeHeaderMenu" />

    <DiscardChangesDialog
        :visible="closeDiscardDialogVisible"
        destructive
        :title="t('matterContext.modal.closeWithoutSavingTitle')"
        :body="t('matterContext.modal.closeWithoutSavingBody')"
        :cancel-label="t('common.cancel')"
        :discard-label="t('matterContext.modal.discardAndClose')"
        @discard="onCloseDiscardConfirm"
        @cancel="onCloseDiscardCancel"
    />

    <DiscardChangesDialog
        :visible="deleteConfirmVisible"
        destructive
        :busy="deleting"
        :title="t('matterContext.modal.deleteTitle')"
        :body="t('matterContext.modal.deleteBody')"
        :cancel-label="t('common.cancel')"
        :discard-label="deleting ? t('matterContext.modal.deleting') : t('matterContext.modal.deleteConfirm')"
        @discard="onDeleteConfirm"
        @cancel="onDeleteCancel"
    />
</template>
